import { Button } from "@/components/ui/button.tsx";
import { MoreHorizontalIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useAvaChat,
  useUpdateAvaChatTitle,
  useDeleteAvaChat,
} from "@/lib/api/ava";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { useState } from "react";
import { useRouter } from "@tanstack/react-router";

interface AvaChatHeaderProps {
  chatId: string;
}

export function AvaChatHeader({ chatId }: AvaChatHeaderProps) {
  const { data: chat } = useAvaChat(chatId);
  const updateTitleMutation = useUpdateAvaChatTitle();
  const deleteChatMutation = useDeleteAvaChat();
  const router = useRouter();

  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleRenameClick = () => {
    // Close dropdown immediately
    setDropdownOpen(false);
    // Use setTimeout to allow dropdown to close first
    setTimeout(() => {
      setNewTitle(chat?.title || "");
      setShowRenameDialog(true);
    }, 100);
  };

  const handleRename = async () => {
    if (!newTitle.trim()) return;

    try {
      await updateTitleMutation.mutateAsync({
        chatId,
        title: newTitle.trim(),
      });
      setShowRenameDialog(false);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleDeleteClick = () => {
    // Close dropdown immediately
    setDropdownOpen(false);
    // Use setTimeout to allow dropdown to close first
    setTimeout(() => {
      setShowDeleteDialog(true);
    }, 100);
  };

  const handleDelete = async () => {
    try {
      await deleteChatMutation.mutateAsync(chatId);
      // Close modal first
      setShowDeleteDialog(false);
      // Small delay before navigation to allow modal to close
      setTimeout(() => {
        router.navigate({ to: "/ava" });
      }, 100);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  return (
    <div className="border-b border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {chat?.title || "AVA Chat"}
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRenameClick();
                }}
              >
                Rename Chat
              </DropdownMenuItem>
              <DropdownMenuItem>Export Chat</DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteClick();
                }}
              >
                Delete Chat
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Rename Dialog - Moved outside dropdown */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Chat</DialogTitle>
            <DialogDescription>
              Give your conversation a more descriptive name.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="chat-title" className="text-sm font-medium">
                Chat Name
              </Label>
              <Input
                id="chat-title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Enter a descriptive name..."
                className="w-full"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleRename();
                  }
                  if (e.key === "Escape") {
                    setShowRenameDialog(false);
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
            <Button
              variant="outline"
              onClick={() => setShowRenameDialog(false)}
              className="mt-2 sm:mt-0"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRename}
              disabled={!newTitle.trim() || updateTitleMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {updateTitleMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog - Moved outside dropdown */}
      <ConfirmActionDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Chat"
        description={`Are you sure you want to delete "${chat?.title}"? This action cannot be undone and all messages will be lost.`}
        loading={deleteChatMutation.isPending}
      />
    </div>
  );
}
